// Real iConnect content from deep audit of iconnect-corp.com
// Captures actual stencil: homepage + 8 sub-pages

// Prefix for static assets in /public so they resolve whether the site is
// served from the domain root or a sub-path (e.g. /corp-next on db3's IIS).
// CRA inlines process.env.PUBLIC_URL at build time ("" for a root build,
// "/corp-next" when built with PUBLIC_URL=/corp-next).
const PUB = process.env.PUBLIC_URL || '';

export const COMPANY = {
  name: 'iConnect',
  fullName: 'iConnect, Inc.',
  established: 2000,
  yearsInBusiness: new Date().getFullYear() - 2000,
  tagline:
    'At iConnect, we connect your business with its trading partners through scalable EDI solutions tailored to your requirements.',
  positioning: 'Value Added Network', // key technical positioning
  brandStatement:
    'When you need EDI, you need it done efficiently and reliably. You need iConnect!',
  address: {
    street: '24 Frank Lloyd Wright Drive',
    poBox: 'P.O. Box 452',
    city: 'Ann Arbor',
    state: 'MI',
    zip: '48106',
    country: 'USA',
  },
  phones: {
    general: '(734) 930-4260',
    fax: '(734) 930-4261',
    salesTollFree: '(877) 327-9334',
    salesDirect: '(734) 930-4274',
    supportTollFree: '(877) 275-7417',
    supportDirect: '(734) 930-4268',
  },
  emails: {
    sales: 'Sales@iConnect-Corp.com',
    support: 'Support@iConnect-Corp.com',
    info: 'Info@iConnect-Corp.com',
    hr: 'HR@iconnect-corp.com',
  },
  socialMedia: {
    twitter: 'https://x.com/iconnect',
    linkedin: 'https://www.linkedin.com/company/iconnect/',
  },
};

/**
 * LINKS - every external URL the site links to, in one place.
 *
 * Swap any value here and it cascades across the entire site (Header, Footer,
 * Careers page, Connections page, CTAs, etc.). Internal React Router routes
 * live in NAVIGATION above and don't need to be in here.
 */
export const LINKS = {
  // External product login
  iExchangeWebLogin: 'https://www.iexchangeweb.com/ieweb/general/login',

  // Email composers (mailto: links with prefilled subject/body)
  careersApply:
    'mailto:HR@iconnect-corp.com?subject=iConnect%20Application',
  requestTradingPartner:
    'mailto:Support@iConnect-Corp.com?subject=Add%20trading%20partner',

  // Documentation / support portals (for future use)
  docsRoot: '#',
  supportPortal: '#',
  statusPage: '#',
};

// Hero copy variants - swap `HERO = HERO_VARIANTS.X` to test different positioning.
// Default is `direct`. Each variant matches the same layout (badges → italic tagline →
// headline + accent → subhead → CTAs → reassurance), only the copy changes.
//
// Note (May 2026): "Trusted since 2000" appears ONCE in the subheading. Earlier
// drafts repeated it in badges + reassurance - that felt sales-heavy. Now badges
// and reassurance carry different concrete value props (partner count, contract
// terms, onboarding speed) so the page doesn't push the same trust signal three times.
//
// Copy note (Jul 2026): `direct` hero copy tuned for automotive EDI / problem-solver
// positioning - marketing to review before launch.
export const HERO_VARIANTS = {
  direct: {
    badges: [
      { text: 'VAN' },
      { text: 'Automotive EDI experts' },
      { text: '24/7 Support' },
      { text: 'Fast onboarding' },
    ],
    italicTagline: 'EDI that just works',
    headline: 'for Automotive Suppliers & Easy Retail EDI',
    headlineAccent: '#1 Partner',
    headlineAccentFirst: true, // blue accent "#1 Partner", then "for Automotive Suppliers & Easy Retail EDI"
    subheading:
      'At iConnect, we connect your business with its trading partners through scalable EDI solutions tailored to your requirements.',
    primaryCTA: 'Get Started',
    secondaryCTA: 'Talk to an Expert',
    reassurance: [
      'Days, not months to onboard',
      'No long-term contracts',
      'No credit card required',
    ],
  },

  // "Without the BS" variant - punchier, dev-friendly tone
  punchy: {
    badges: [
      { text: 'No long-term contracts' },
      { text: 'VAN' },
      { text: '5-day onboarding' },
    ],
    italicTagline:
      'Connecting you to your customers and your suppliers with simple, complete and cost-effective EDI solutions.',
    headline: 'EDI without the',
    headlineAccent: 'baggage.',
    subheading:
      "Onboard your first trading partner in days, not months. Flat-rate VAN, 24/7 support, real engineers on the phone.",
    primaryCTA: 'Start free',
    secondaryCTA: 'See it work',
    reassurance: [
      'No credit card required',
      'Cancel any month',
      '24/7 humans, not bots',
    ],
  },

  // Scale-focused variant - for bigger enterprises
  scale: {
    badges: [
      { text: '39,000+ partners' },
      { text: 'VAN' },
      { text: 'MMOG/LE compliant' },
    ],
    italicTagline:
      'Connecting you to your customers and your suppliers with simple, complete and cost-effective EDI solutions.',
    headline: 'The EDI backbone of',
    headlineAccent: 'global manufacturing.',
    subheading:
      "100,000+ documents per day across 39,000+ trading partners. Trusted by Fortune 500 manufacturers, suppliers at every tier, and major retailers since 2000.",
    primaryCTA: 'Schedule a Demo',
    secondaryCTA: 'See architecture',
    reassurance: [
      'GM/Ford MMOG/LE compliant',
      '99.9% uptime',
    ],
  },
};

// Active hero - swap variant key here to ship a different headline
export const HERO = HERO_VARIANTS.direct;

// Navigation matches the reference site at /iConnect New Website/Header.html
// Order: ABOUT US → SOLUTIONS → CASE STUDIES → RESOURCES → CONNECTIONS → CONTACT US
// `to` values are React Router routes (handled in App.js).
export const NAVIGATION = [
  {
    label: 'About Us',
    to: '/about',
    children: [
      { label: 'Company Profile', to: '/about' },
      { label: 'Management', to: '/about/management' },
      { label: 'Careers', to: '/about/careers' },
    ],
  },
  {
    label: 'Solutions',
    to: '/solutions',
    children: [
      { label: 'iExchangeWeb', to: '/solutions/iexchangeweb' },
      { label: 'Managed EDI Services', to: '/solutions/managed-edi' },
      { label: 'VAN Services', to: '/solutions/van' },
      { label: 'Supplier EDI', to: '/solutions/supplier-edi' },
      { label: 'Hybrid VAN', to: '/solutions/hybrid-van' },
      { label: 'OFTP2', to: '/solutions/oftp2' },
      { label: 'Case Studies', to: '/case-studies' },
    ],
  },
  // Case Studies is also a standalone top-level tab (in addition to the link
  // inside Solutions above). Glossary + Connections promoted from the old
  // "Resources" dropdown to their own tabs. Routes stay /resources/* & /case-studies.
  { label: 'Case Studies', to: '/case-studies' },
  { label: 'Connections', to: '/resources/connections' },
  { label: 'Compare', to: '/compare' },
  // Glossary hidden for now - uncomment to restore (also its App.js import + route and the Footer link).
  // { label: 'Glossary', to: '/resources/glossary' },
  { label: 'Contact Us', to: '/contact' },
];

// Welcome section - brand positioning between hero and solutions
export const WELCOME = {
  eyebrow: 'Welcome to iConnect',
  pullQuote: 'When you need EDI, you need it done efficiently and reliably. You need iConnect!',
  intro:
    "Established in 2000 in Ann Arbor, Michigan, iConnect is a technology service provider with deep expertise in EDI and trading partner integration - built for the way modern manufacturers, distributors, and retailers actually do business.",
  features: [
    '24/7 Support',
    'No long-term contracts',
    'Flexible subscription options',
    'Hybrid VAN capability',
    'Any-to-Any data transformation',
    '36 months self-service archiving',
  ],
  quickActions: [
    {
      label: 'Contact Us',
      description: 'Talk to our team',
      icon: 'Mail',
      href: '/contact',
    },
    {
      label: 'Speak to Sales',
      description: 'Sales@iConnect-Corp.com',
      icon: 'Phone',
      href: 'mailto:Sales@iConnect-Corp.com',
    },
    {
      label: 'How Does iConnect Compare?',
      description: 'See the difference',
      icon: 'Scale',
      href: '/compare',
    },
    {
      label: 'iExchangeWeb Login',
      description: 'Access your account',
      icon: 'LogIn',
      // External link (intentionally string-literal here so this object stays serializable)
      href: 'https://www.iexchangeweb.com/ieweb/general/login',
    },
  ],
};

// Solutions - expanded with full real feature lists from sub-pages
export const SOLUTIONS = [
  {
    id: 'managed-edi',
    title: 'Managed EDI Services',
    subtitle: 'Hosted, integrated, hands-off',
    description:
      'A hosted EDI solution seamlessly integrated with your ERP. Single source for transport, translation, and integration across all your trading partners.',
    features: [
      'Fully hosted, no software to install',
      'Single source: transport, translation, integration',
      'Integration for all trading partners and documents',
      'Monthly subscription, no long-term contract',
      'Real-time, event-driven solution',
      'Email notifications of time-critical events',
      'Automated email alerts for rejected transactions',
      '24/7 support',
      "iConnect's VAN included",
      'Both customer and supplier EDI',
      'Any-to-Any data format mapping',
      'Direct REST/SOAP API connections',
      'Web Forms (human-readable + ASN backup)',
    ],
    icon: 'Server',
    accent: '#00a8a8',
    pdf: PUB + '/pdf/Managed Services.pdf',
  },
  {
    id: 'iexchangeweb',
    title: 'iExchangeWeb',
    subtitle: 'Web EDI made simple',
    description:
      'Making EDI over the Web simple, effective, and economical. Go live with customers in a matter of days - no software to install, no infrastructure to maintain.',
    features: [
      'Easy access via the Web',
      'No software or hardware to install',
      'Monthly subscription, no long-term contract',
      '24/7 support',
      'Supports all your customer requirements',
      'Go live in a matter of days',
      'Web Forms customized to customer EDI requirements',
      'Email notifications of inbound documents',
      'ASN backup with automotive OEMs',
      'Supports OEM shipping/label requirements for all major OEMs',
      'Easy upgrade to integrated solution',
    ],
    icon: 'Globe',
    accent: '#00d4d4',
    pdf: PUB + '/pdf/iExchangeWeb.pdf',
  },
  {
    id: 'van',
    title: 'VAN Services',
    subtitle: 'Value Added Network',
    description:
      'Transport and manage your EDI data on our VAN. Simple, predictable pricing - flat rate, no surcharges, no surprises.',
    features: [
      'User and trading partner management',
      'Simple, predictable, affordable pricing',
      'Month-to-month, no long-term commitments',
      'Interconnections to all major VANs',
      'Best uptime in the industry',
      'Instantly restore archived documents (up to 36 months)',
      'Hybrid document review (human-readable)',
      'Multi-format data transport',
      'Web-form transmission backup',
      'ASN self-certification',
      'Immediate access to your mailbag ID',
      'Email alerts for critical events',
      '24/7 support',
    ],
    icon: 'Network',
    accent: '#2dbfbf',
  },
  {
    id: 'supplier-edi',
    title: 'Supplier EDI',
    subtitle: 'Onboard your supply chain',
    description:
      'Successfully connect to your vendors and suppliers. We handle the project management, supplier onboarding, and GM/Ford MMOG/LE compliance.',
    features: [
      'Hosted environment, no software to install',
      'Single source for transport / translation / integration',
      'Integration with your ERP',
      'GM/Ford MMOG/LE compliance',
      'Complete project management',
      'We contact and onboard your suppliers',
      'Monthly subscription, no long-term contract',
      'Real-time, event-driven',
      '24/7 support',
      "iConnect's VAN",
      'Web Forms (human-readable backup)',
    ],
    icon: 'Truck',
    accent: '#00d4d4',
  },
];

// Solutions not in the master SOLUTIONS array but referenced from the nav
export const EXTENDED_SOLUTIONS = {
  oftp2: {
    id: 'oftp2',
    title: 'OFTP2',
    subtitle: 'Secure, standards-based file transfer',
    description:
      'OFTP2 (Odette File Transfer Protocol v2) is a secure, standards-based protocol for exchanging EDI and business documents directly between trading partners. It layers strong encryption, digital signatures, and file compression on top of reliable delivery, so you can meet the OFTP2 requirements of any partner that mandates it, whether over the internet or through our VAN.',
    features: [
      'OFTP2 (Odette FTP version 2) compliant',
      'TLS 1.2 encryption',
      'Digital signatures and acknowledgments',
      'Native compression for large files',
      'Direct-to-partner or via VAN',
      'Ready for any OFTP2 trading partner',
      'Audit trail and message tracking',
      '24/7 support',
    ],
    icon: 'Cable',
  },
  'hybrid-van': {
    id: 'hybrid-van',
    title: 'Hybrid VAN',
    subtitle: 'Best of both worlds',
    // Approved copy supplied by the iConnect team (only "Hybrid Mailbox
    // Solution" -> "Hybrid VAN" changed so the body matches the section title).
    description:
      'Hybrid VAN provides traditional EDI support with added EDI translation and form support when a trading-partner subscription is in place. Customers can utilize this functionality as a trading-partner backup solution, or as a measure to implement new trading-partner transactions more rapidly while completing internal mapping projects. A trading partner may require EDI compliance that will require extended implementation, and this will provide an EDI solution to be compliant rapidly.',
    features: [
      'VAN baseline included',
      'Best uptime in the industry',
      'Added EDI translation and forms support',
      'Direct AS2 to high-volume partners',
      'Trading-partner backup and rapid go-live',
      'Single-pane document tracking',
      'Automatic failover between methods',
      'Predictable flat-rate pricing',
      'No surcharges on either path',
      '36-month archive across both',
      '24/7 support',
    ],
    icon: 'Layers',
  },
};

// Real testimonials (verified from iconnect-corp.com homepage)
export const TESTIMONIALS = [
  {
    id: 1,
    quote:
      'Switching to iConnect was a very easy transition - within a week all connections with customers were established. Staff available 24/7.',
    name: 'Sharon W.',
    title: 'Materials Manager',
    company: 'SA Automotive',
    initials: 'SW',
  },
  {
    id: 2,
    quote:
      'iConnect has complete control of requests and EDI processes. Well over 99% uptime, support quick and responsive.',
    name: 'Diane P.',
    title: 'EDI Analyst',
    company: 'FCC',
    initials: 'DP',
  },
  {
    id: 3,
    quote:
      'Long-standing partner helping save time and money. Support staff extremely helpful and quick to respond.',
    name: 'Renee H.',
    title: 'eBusiness Manager',
    company: 'Veritiv',
    initials: 'RH',
  },
  {
    id: 4,
    quote:
      "Everyone super nice, responsive, made transition seamless. Highly recommend iConnect's service.",
    name: 'Michelle Savina',
    title: 'Customer Concierge II',
    company: 'Imperial Graphics',
    initials: 'MS',
  },
  {
    id: 5,
    quote:
      'Trusted partner for many years. Support unmatched with requests fielded almost immediately.',
    name: 'Wyatt M.',
    title: 'Inventory Analyst',
    company: 'National Engineered Fasteners, Inc.',
    initials: 'WM',
  },
];

// Real named case-study customers (from CaseStudies.html + new site dropdown audit)
export const CASE_STUDY_CUSTOMERS = [
  { name: 'STRATTEC', industry: 'Automotive Locks & Keys', blurb: "World's largest producer of mechanical and electronic door locks and keys" },
  { name: 'Stryker', industry: 'Medical Technology', blurb: 'Joint replacements, surgical instruments, and hospital equipment' },
  { name: 'INOAC', industry: 'Automotive Exteriors', blurb: 'Spoilers, roof moldings, and ground effects for OEMs' },
  { name: 'J.L. French', industry: 'Die Cast Aluminum', blurb: 'Power train components for automotive manufacturers' },
  { name: 'Continental', industry: 'Tier 1 Automotive', blurb: 'Brake systems, electronics, and tire technology' },
  { name: 'Arnecom', industry: 'Automotive Interior', blurb: 'Seat covers and interior trim systems' },
  { name: 'Webasto-Edscha Cabrio', industry: 'Automotive (DE)', blurb: 'Convertible roofs, body systems, climate control' },
  { name: 'FTE Automotive', industry: 'Automotive (DE)', blurb: 'Drive train and brake system applications' },
  { name: 'National Engineered Fasteners', industry: 'Industrial Fasteners', blurb: 'Appliance, alternative energy, military, and aerospace' },
];

// Industries iConnect serves - used by the home page CustomersSection.
// We deliberately avoid showing specific brand logos (trademark licensing
// risk) and instead represent reach through industry breadth + scale.
// Numbers are approximate "well over" counts representative of the network.
export const INDUSTRY_PRESENCE = [
  {
    name: 'Automotive OEMs',
    description: 'Major North American, European, and Asian automakers - including EV-native manufacturers',
    partnerCount: '50+',
    icon: 'Building2',
  },
  {
    name: 'Suppliers · All Tiers',
    description: 'Tier 1, 2 & 3 drivetrain, brake, electrical, interior, and exterior component makers',
    partnerCount: '120+',
    icon: 'Package',
  },
  {
    name: 'Industrial & Heavy',
    description: 'Heavy equipment, appliance, fastener, and motorcycle manufacturers',
    partnerCount: '90+',
    icon: 'Truck',
  },
  {
    name: 'Retail',
    description: 'Home improvement, electronics, department stores, and e-commerce platforms',
    partnerCount: '40+',
    icon: 'ShoppingBag',
  },
];

// Curated customer logos - RETAINED ONLY for the /resources/connections page
// where users search the trading-partner spec list. NOT used on the home page
// anymore (logos removed pending licensing review).
export const CUSTOMER_LOGOS = [
  // Automotive OEMs
  { name: 'Ford', file: 'ford.jpg', industry: 'OEM' },
  { name: 'GM', file: 'generalmotors.jpg', industry: 'OEM' },
  { name: 'Toyota', file: 'toyota.jpg', industry: 'OEM' },
  { name: 'Honda', file: 'honda.jpg', industry: 'OEM' },
  { name: 'BMW', file: 'bmw.jpg', industry: 'OEM' },
  { name: 'Mercedes', file: 'mercedes.jpg', industry: 'OEM' },
  { name: 'Hyundai', file: 'hyundai.jpg', industry: 'OEM' },
  { name: 'Nissan', file: 'nissan.jpg', industry: 'OEM' },
  { name: 'Jaguar Land Rover', file: 'JaguarLandRover.jpg', industry: 'OEM' },
  { name: 'Lucid', file: 'Lucid.jpg', industry: 'OEM (EV)' },
  { name: 'Rivian', file: 'Rivian.jpg', industry: 'OEM (EV)' },
  // Tier 1 / Industrial suppliers
  { name: 'Bosch', file: 'bosch.jpg', industry: 'Tier 1' },
  { name: 'Magna', file: 'magna.jpg', industry: 'Tier 1' },
  { name: 'Aptiv', file: 'aptiv.png', industry: 'Tier 1' },
  { name: 'BorgWarner', file: 'borgwarner.jpg', industry: 'Tier 1' },
  { name: 'Faurecia', file: 'faurecia.jpg', industry: 'Tier 1' },
  { name: 'Lear', file: 'lear.jpg', industry: 'Tier 1' },
  { name: 'Visteon', file: 'visteon.jpg', industry: 'Tier 1' },
  { name: 'Denso', file: 'DensoNA.jpg', industry: 'Tier 1' },
  { name: 'Yazaki', file: 'yazaki.gif', industry: 'Tier 1' },
  // Industrial / Heavy
  { name: '3M', file: '3m.jpg', industry: 'Industrial' },
  { name: 'GE Appliances', file: 'GEAppliances.jpg', industry: 'Industrial' },
  { name: 'Whirlpool', file: 'whirlpool.jpg', industry: 'Industrial' },
  { name: 'Caterpillar', file: 'caterpillar.jpg', industry: 'Heavy Industry' },
  { name: 'John Deere', file: 'johndeere.jpg', industry: 'Heavy Industry' },
  { name: 'Owens Corning', file: 'owenscorning.jpg', industry: 'Industrial' },
  { name: 'Black & Decker', file: 'blackdecker.jpg', industry: 'Industrial' },
  { name: 'Briggs & Stratton', file: 'briggsstratton.jpg', industry: 'Industrial' },
  { name: 'Harley-Davidson', file: 'harleydavidson.jpg', industry: 'Motorcycles' },
  { name: 'Kimberly-Clark', file: 'kimberlyclark.jpg', industry: 'Consumer' },
  // Retail
  { name: 'Home Depot', file: 'HomeDepot.jpg', industry: 'Retail' },
  { name: 'Macy\'s', file: 'Macys.jpg', industry: 'Retail' },
  { name: 'JCPenney', file: 'jcpenney.jpg', industry: 'Retail' },
  { name: "Kohl's", file: 'kohls.jpg', industry: 'Retail' },
  { name: 'Best Buy', file: 'bestbuy.jpg', industry: 'Retail' },
  { name: "Lowe's", file: 'lowes.jpg', industry: 'Retail' },
  { name: 'Sears', file: 'sears.jpg', industry: 'Retail' },
];

// Compare section - interactive yes/no comparison with iConnect's answer
// revealed on click. Each question has the iConnect-side answer baked in
// so users see the contrast immediately.
export const COMPARE = {
  eyebrow: 'How Does iConnect Compare?',
  headline: "Ask your current provider these questions.",
  questions: [
    {
      q: 'Does your provider offer best-available pricing on month-to-month subscriptions, with no long-term contract?',
      iConnect: 'Yes - month-to-month subscriptions, no annual lock-in.',
    },
    {
      q: 'Does your VAN charge a flat rate for traffic, with no surcharges?',
      iConnect: "Yes - flat-rate pricing with predictable monthly cost. No per-document surcharges, no peak overages.",
    },
    {
      q: 'Can you instantly restore archived documents up to 36 months as needed?',
      iConnect: 'Yes - 36 months of self-service archive, restored on demand from your portal.',
    },
    {
      q: 'Can your users control access to documents by trading partner and document type?',
      iConnect: 'Yes - granular role/permission controls per partner and document type.',
    },
    {
      q: 'Do you get email alerts the moment a new document arrives?',
      iConnect: 'Yes - real-time email notifications, configurable per partner and event type.',
    },
    {
      q: 'Do you get email alerts the moment a transaction is rejected?',
      iConnect: 'Yes - automated alerts on rejected transactions, so your team can fix and resubmit before a partner ever calls.',
    },
    {
      q: 'Can you view your EDI in a human-readable format, on demand?',
      iConnect: "Yes - built-in human-readable views. Don't need a developer to read an 856.",
    },
    {
      q: 'Can you add a new trading partner without enterprise procurement cycles?',
      iConnect: 'Yes - most partners go live in days, not months. Often same-week.',
    },
  ],
  // Competitor matrix - concrete differentiators
  matrix: {
    headers: ['', 'iConnect', 'Generic VAN', 'Big-Brand VAN'],
    rows: [
      ['Month-to-month contract', 'yes', 'sometimes', 'rare'],
      ['Flat-rate pricing (no surcharges)', 'yes', 'no', 'no'],
      ['36-month self-service archive', 'yes', 'partial', 'partial'],
      ['Human-readable doc viewer', 'yes', 'extra cost', 'extra cost'],
      ['24/7 human support', 'yes', 'business hours', 'tiered support'],
      ['Onboarding speed', 'days', 'weeks', 'months'],
      ['MMOG/LE compliance help', 'yes', 'no', 'sometimes'],
    ],
  },
  closer: "If the answer to any of these is \"No\" - May be time to evaluate a more flexible EDI provider.",
  closerCTA: 'Talk to Sales',
};

// Leadership team (from Management.html audit)
export const LEADERSHIP = [
  {
    name: 'Kishor Sapre',
    title: 'Founder & CEO',
    bio: 'Co-founded iConnect after 12+ years in high-tech. Previously Senior Director of Development at Harbinger and part of Premenos pre-IPO management.',
    education: 'MS, Purdue · BS, Ohio State (Summa Cum Laude)',
    initials: 'KS',
    photo: PUB + '/management/kishor_sapre.jpg',
  },
  {
    name: 'Steve Petty',
    title: 'Chief Technical Officer',
    bio: 'First man through the door 25+ years ago. Architected iExchangeWeb. Oversees infrastructure, systems, networking, and database management.',
    education: 'Previously: Supply Tech, Harbinger',
    initials: 'SP',
  },
  {
    name: 'Jonathan Cisneros',
    title: 'Global Sales',
    bio: '15+ years of experience across automotive sales, strategic business development, engineering background and program management.',
    education: 'MS, Iberoamericana. Global Sales',
    initials: 'JC',
    photo: PUB + '/management/jonathan.png',
  },
  {
    name: 'Kelli Sloan',
    title: 'Director, Customer Services',
    bio: 'Joined 2007 with 12+ years in E-Commerce Automotive. Previously EDI Office Manager at Advanced Technologies.',
    education: 'Supplier & manufacturer liaison specialist',
    initials: 'KS',
    photo: PUB + '/management/kelli_sloane.jpg',
  },
  {
    name: 'Kartheik Godavarthi',
    title: 'Manager, Implementation Services',
    bio: 'Joined 2012. Manages analysis, design, mapping, and custom implementations.',
    education: 'MBA + MS Information Systems, Eastern Michigan U.',
    initials: 'KG',
    photo: PUB + '/management/Kartheik_Godavarthi.jpg',
  },
  {
    name: 'Spencer Schauer',
    title: 'Software Development Manager',
    bio: 'Started as intern May 2015, advanced to lead the development team and oversee infrastructure maintenance.',
    education: 'BS, Computer & Information Systems Security, Eastern Michigan U.',
    initials: 'SS',
    photo: PUB + '/management/spencer.jpg',
  },
];

export const VALUE_PROPS = [
  {
    icon: 'Headphones',
    title: '24/7 Support',
    description:
      'Around-the-clock dedicated support team ready to help whenever you need it.',
  },
  {
    icon: 'FileText',
    title: 'No Long-Term Contracts',
    description:
      'Month-to-month subscriptions. Stay because you love us, not because you have to.',
  },
  {
    icon: 'Repeat',
    title: 'Any-to-Any Transformation',
    description:
      'Convert between any EDI format or standard seamlessly - e.g. X12, EDIFACT, XML, CSV, and any custom format.',
  },
  {
    icon: 'Archive',
    title: '36 Months Archiving',
    description:
      'Self-service access to 3 years of EDI history - instantly restore for compliance and audits.',
  },
  {
    icon: 'Network',
    title: 'VAN',
    description:
      'Best-in-class Value Added Network with interconnections to all major VANs - flat-rate pricing, no surcharges.',
  },
  {
    icon: 'Zap',
    title: 'Days, Not Months',
    description:
      'New trading partners connected in days. ASN self-certification and ASN backup with automotive OEMs.',
  },
];

// Real numbers from the new iconnect.html (counter widgets):
//   36 months recoverable data | 100,000 docs/day | 39,000 relationships | 1,100 web forms
export const TRUST_INDICATORS = [
  // Numbers removed per feedback (some figures were unverified). Each item is
  // now anchored by an icon instead of an animated count; labels/copy retained.
  { icon: 'Files', label: 'Documents Daily', sublabel: 'Processed across our VAN' },
  { icon: 'Network', label: 'Active Relationships', sublabel: 'Trading partner connections' },
  { icon: 'LayoutTemplate', label: 'Custom Web Forms', sublabel: 'Tailored to customer EDI requirements' },
  { icon: 'RotateCcw', label: 'Recoverable Data', sublabel: 'Instant self-service restore' },
];

export const EDI_PROCESS_STEPS = [
  {
    step: 1,
    title: 'Send',
    description:
      'Your business system generates an order, invoice, or shipment notice in any format.',
    icon: 'Upload',
    detail: 'e.g. XML · CSV · X12 · EDIFACT · any format',
  },
  {
    step: 2,
    title: 'Transform',
    description:
      'iConnect translates your data to the EDI standards your partner needs - automatically.',
    icon: 'Repeat',
    detail: 'Any-to-Any data transformation',
  },
  {
    step: 3,
    title: 'Deliver',
    description:
      'Securely transmit through our VAN with full audit trail and 99.9% uptime.',
    icon: 'Send',
    detail: 'VAN · Hybrid AS2 · No surcharges',
  },
  {
    step: 4,
    title: 'Confirm',
    description:
      'Get instant acknowledgments, email alerts, and full visibility into every transaction.',
    icon: 'CheckCircle',
    detail: 'Real-time alerts · 36-month archive',
  },
];

// Standards that iConnect actually transmits / supports as a VAN.
// These are technical EDI standards (transport + document formats), NOT
// regulatory compliance certifications. We deliberately avoid claims like
// "HIPAA compliant" or "SOC 2 certified" unless iConnect can produce the
// audit report - those need explicit confirmation from operations before
// adding back.
export const COMPLIANCE_BADGES = [
  'X12',
  'EDIFACT',
  'AS2',
  'OFTP2',
  'sFTP',
  'GM/Ford MMOG/LE',
];

// Detailed case studies for the /case-studies accordion page.
//
// Schema (May 2026 rewrite for substance):
//   - companyDescription: who they are (1 line, factual)
//   - challenge: the EDI-specific problem they faced (NOT a description of
//     their business - what made them call iConnect)
//   - solution: what iConnect specifically did (NOT a generic VAN pitch)
//   - outcome: the measurable result
//   - metric / metricLabel: small headline stat for the bento card
//
// Source notes: company descriptions verbatim from
// iconnect-corp.com/CaseStudies.html. Challenge/solution/outcome text is
// drafted from the customer profile + standard iConnect deliverables; the
// production team should review each case study against the linked PDF
// whitepapers (paths under /pdf/) for accuracy before publishing.
export const CASE_STUDIES_DETAILED = [
  {
    id: 'inoac',
    name: 'INOAC',
    industry: 'Automotive Exteriors',
    companyDescription:
      'Midwest-based manufacturer of exterior options - spoilers, roof and body-side moldings, ground effects - that attach to car bodies for design customization.',
    challenge:
      'Multiple OEM trading partners across a multi-plant footprint, each with different EDI envelope requirements and ASN timing rules. Onboarding new partners was eating engineering cycles.',
    solution:
      "iConnect's managed VAN took ownership of envelope, transport, and partner onboarding - INOAC plants ship one canonical document format and iConnect handles per-partner translation.",
    outcome:
      'Engineering team freed from EDI plumbing work. Partner onboarding moved from a multi-week IT project to a few-day configuration task.',
    metric: '200+',
    metricLabel: 'Trading partners',
    pdf: PUB + '/pdf/INOAC.pdf',
    logo: PUB + '/vendor-logos/inoacexteriorproducts.gif',
  },
  {
    id: 'strattec',
    name: 'STRATTEC',
    industry: 'Automotive Security & Access',
    companyDescription:
      "World's largest producer of mechanical and electronic door locks and keys, ignition lock housings, sliding door systems, and power lift gates for global automotive manufacturers.",
    challenge:
      'Global automotive OEM trading partners demand reliable EDI 24/7 with high-volume document throughput. Outage windows at the VAN level disrupt just-in-time manufacturing.',
    solution:
      "iConnect's VAN with 24/7 monitoring and immediate human support. Email alerts on time-critical events so STRATTEC's EDI team gets notified before partner contacts them.",
    outcome:
      'Sustained high availability across global OEM connections, with proactive notifications instead of reactive firefighting.',
    metric: 'Tier 1',
    metricLabel: 'VAN reliability',
    pdf: PUB + '/pdf/STRATTEC.pdf',
    logo: PUB + '/vendor-logos/strattec.jpg',
  },
  {
    id: 'stryker',
    name: 'Stryker',
    industry: 'Medical Technology',
    companyDescription:
      'Global leader in medical technology - joint replacement systems, endoscopic cameras, surgical instruments, and hospital beds.',
    challenge:
      "Stryker's hospital and distributor partners use a wide mix of EDI formats. Coordinating new trading partner setup was slow because each one needed custom mapping work.",
    solution:
      "iConnect's iExchangeWeb forms gave Stryker's partners a web interface for sending/receiving EDI without installing software. iConnect handles the format translation behind the scenes.",
    outcome:
      'Partner onboarding cycle dropped from weeks to days. Stryker scaled trading partner connections without scaling EDI headcount.',
    metric: '5 days',
    metricLabel: 'Avg. onboarding',
    pdf: PUB + '/pdf/Stryker.pdf',
    logo: PUB + '/vendor-logos/stryker.bmp',
  },
  {
    id: 'jlfrench',
    name: 'J.L. French',
    industry: 'Die Cast Aluminum',
    companyDescription:
      'Leading global supplier of die-cast aluminum components for powertrain applications, with combined North American and European manufacturing operations.',
    challenge:
      'Cross-Atlantic operation with North American X12 trading partners AND European EDIFACT/OFTP2 partners. Maintaining two separate EDI stacks was costly and error-prone.',
    solution:
      "iConnect consolidated NA + EU EDI traffic onto a single platform. iConnect's translator handles X12 ↔ EDIFACT conversion; OFTP2 connections supported natively for European OEMs.",
    outcome:
      'Single source of truth for all global EDI. Significant reduction in vendor cost and support overhead vs. running two regional VANs.',
    metric: '2 → 1',
    metricLabel: 'Stacks consolidated',
    pdf: PUB + '/pdf/JL French.pdf',
    logo: PUB + '/vendor-logos/jlfrench.JPG',
  },
  {
    id: 'webedcab',
    name: 'Webasto-Edscha Cabrio',
    industry: 'Convertible Roof Systems',
    companyDescription:
      'International automotive supplier (HQ near Munich, 50+ locations worldwide) - convertible roof/body systems plus heating, cooling, and air-conditioning.',
    challenge:
      'European OEM customers required OFTP2 connectivity, while North American facilities needed X12 over a VAN. Bridging the two was operationally complex.',
    solution:
      'iConnect provided OFTP2 endpoints for European partners and VAN connectivity for North America, with consolidated tracking and archiving across both protocols.',
    outcome:
      'One iConnect dashboard for global EDI traffic, regardless of underlying transport. European compliance maintained alongside NA reliability.',
    metric: 'OFTP2',
    metricLabel: 'EU compliance',
    pdf: PUB + '/pdf/Webasto-Edscha Cabrio.pdf',
    logo: PUB + '/vendor-logos/webasto.jpg',
  },
  {
    id: 'fteauto',
    name: 'FTE Automotive',
    industry: 'Drivetrain & Brake Systems',
    companyDescription:
      'German automotive manufacturer specializing in drivetrain and brake systems - 3,800+ employees across 11 production sites globally.',
    challenge:
      'Migrating from a legacy VAN provider while continuing OEM EDI compliance for active model-year programs. Cutover risk was a hard blocker.',
    solution:
      "iConnect's migration playbook: parallel-run on both VANs, validated traffic on iConnect for the same partner, then cut DNS/routing per partner with rollback ready.",
    outcome:
      'Migration completed with zero documented downtime per FTE - OEM partner SLAs maintained throughout the transition window.',
    metric: '0',
    metricLabel: 'Downtime',
    pdf: PUB + '/pdf/FTE Automotive.pdf',
    logo: PUB + '/vendor-logos/fteautomotive.jpg',
  },
  {
    id: 'nef',
    name: 'National Engineered Fasteners',
    industry: 'Industrial Fasteners',
    companyDescription:
      "Founded 1996. Specialty and standard fasteners - North America's leading appliance-industry fastener supplier; approved for alternative energy, military, and aerospace.",
    challenge:
      'Mid-sized supplier needed enterprise-grade EDI without enterprise-grade headcount. They wanted help responding fast when a partner spec changed.',
    solution:
      "iConnect's hands-on support team became NEF's outsourced EDI desk - request changes get implemented quickly, no ticket queues.",
    outcome:
      'Long-running partnership where NEF treats iConnect as a member of their internal team. Continuous renewal year over year.',
    metric: '20+',
    metricLabel: 'Years partnered',
    pdf: PUB + '/pdf/NEF.pdf',
  },
  {
    id: 'arnecom',
    name: 'Arnecom',
    industry: 'Automotive Wire Harnesses',
    companyDescription:
      'Latin American manufacturer of automotive wire harnesses, copper wire, components and metering equipment - Yazaki Corporation subsidiary with 10,000+ employees across Mexico, Nicaragua and IT services for Yazaki Brazil, Argentina and Uruguay. Customers include Chrysler, Ford, Nissan, Toyota and GM.',
    challenge:
      'Yazaki restructured procurement - Arnecom shifted from 70% affiliate-managed purchasing to direct supplier procurement, requiring EDI rollout with 90 trading partners on a very compressed timeline.',
    solution:
      'iExchangeWeb deployment with custom programming to extend Arnecom\'s limited proprietary EDI apps. iConnect handled implementation support, guidelines definition, mapping, and ongoing VAN connectivity as a single-vendor package.',
    outcome:
      'EDI implementation with 90 trading partners started in 11 days. 30+ additional non-EDI partners later requested moving to iExchangeWeb. After-hours and weekend support eliminated the communication problems Arnecom had with prior VANs.',
    metric: '11 days',
    metricLabel: 'To 90 partners',
    pdf: PUB + '/pdf/Arnecom.pdf',
    logo: PUB + '/vendor-logos/arnecom.gif',
  },
  {
    id: 'continental',
    name: 'Continental',
    industry: 'Tier 1 Automotive Supplier',
    companyDescription:
      'Continental Automotive Systems - Tier 1 supplier of cellular technology embedded into GM\'s OnStar and in-car communications for Ford, Chrysler, BMW, and Volvo. The division was spun off from Motorola, which forced a complete VAN + EDI software replacement.',
    challenge:
      'Six-month corporate-mandated deadline to replace ALL VAN connectivity and EDI translation software for 46 major customers across 7 different VAN carriers plus direct lines to Ford and Chrysler - roughly 180 distinct formats - while running production uninterrupted.',
    solution:
      'iConnect rebuilt the entire EDI infrastructure on a new set of SAP enterprise applications operating in the cloud (SaaS), with a custom backend handling all supplier translations. Deep automotive experience meant many partner formats and layouts were already known.',
    outcome:
      'Migration completed inside the requested time window. Sustained VAN uptime of 99.97%+. Two years of human-readable EDI archive directly accessible to Continental users. Continued proactive monitoring and advocacy from iConnect\'s named-contact support model.',
    metric: '99.97%',
    metricLabel: 'VAN uptime',
    pdf: PUB + '/pdf/Continental.pdf',
    logo: PUB + '/vendor-logos/continentalteves.jpg',
  },
];
